import { type FC, Suspense, useState } from "react";
import { Form } from "react-bootstrap";
import { ErrorBoundary } from "react-error-boundary";
import type { MetaAdsReportBranding } from "shared";
import { Icon } from "../common/Icon";
import { useModal } from "../common/modal/Modal";
import { Card } from "../components/Card";
import { AppPage } from "../layout/AppLayout";
import { useRegisterMetaAdsUnsavedWork } from "./metaAdsUnsavedWork";
import { ModalCreateSchedule } from "./reports/ModalCreateSchedule";
import { ModalCreateTemplate } from "./reports/ModalCreateTemplate";
import { useCanManageMetaAds } from "./useMyMetaAdsRole";
import {
	useReportActions,
	useReportBranding,
	useReportSchedules,
	useReportTemplates,
} from "./useReports";

const TemplatesTab: FC = () => {
	const { data: templates } = useReportTemplates();
	const { createTemplate, updateTemplate, deleteTemplate } = useReportActions();
	const { open } = useModal();

	const handleCreate = () => {
		open(ModalCreateTemplate, {
			onSave: async (dto) => {
				await createTemplate.mutateAsync(dto);
			},
		});
	};

	const handleEdit = (template: (typeof templates)[number]) => {
		open(ModalCreateTemplate, {
			initial: template,
			onSave: async (dto) => {
				await updateTemplate.mutateAsync({ id: template.id, dto });
			},
		});
	};

	return (
		<Card>
			<Card.Header>
				Report Templates
				<button
					type="button"
					className="btn btn-sm btn-primary"
					style={{ marginLeft: "auto" }}
					onClick={handleCreate}
				>
					<Icon name="add" size={16} /> Create Template
				</button>
			</Card.Header>
			<Card.Body style={{ padding: 0 }}>
				{templates.length === 0 ? (
					<p className="text-muted" style={{ padding: 16, marginBottom: 0 }}>
						No templates yet. Create your first report template.
					</p>
				) : (
					<table className="table" style={{ marginBottom: 0 }}>
						<thead>
							<tr>
								<th>Name</th>
								<th>Sections</th>
								<th>Date Preset</th>
								<th>Comparison</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{templates.map((t) => (
								<tr key={t.id}>
									<td>{t.name}</td>
									<td>{t.sections.length} sections</td>
									<td>{t.datePreset.replace(/_/g, " ")}</td>
									<td>
										{t.comparisonEnabled ? (
											<span className="badge bg-success">On</span>
										) : (
											<span className="badge bg-secondary">Off</span>
										)}
									</td>
									<td>
										<div className="d-flex gap-1">
											<button
												type="button"
												className="btn btn-sm btn-outline-primary"
												onClick={() => handleEdit(t)}
											>
												Edit
											</button>
											<button
												type="button"
												className="btn btn-sm btn-outline-danger"
												onClick={() => deleteTemplate.mutate(t.id)}
												disabled={deleteTemplate.isPending}
											>
												Delete
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Card.Body>
		</Card>
	);
};

const SchedulesTab: FC = () => {
	const { data: schedules } = useReportSchedules();
	const { data: templates } = useReportTemplates();
	const { createSchedule, updateSchedule, deleteSchedule } = useReportActions();
	const { open } = useModal();

	const handleCreate = () => {
		if (templates.length === 0) return;
		open(ModalCreateSchedule, {
			templates,
			onSave: async (dto) => {
				await createSchedule.mutateAsync(dto);
			},
		});
	};

	const handleToggle = (schedule: (typeof schedules)[number]) => {
		updateSchedule.mutate({
			id: schedule.id,
			dto: {
				templateId: schedule.templateId,
				frequency: schedule.frequency,
				dayOfWeek: schedule.dayOfWeek,
				dayOfMonth: schedule.dayOfMonth,
				recipients: schedule.recipients,
				enabled: !schedule.enabled,
			},
		});
	};

	return (
		<Card>
			<Card.Header>
				Report Schedules
				<button
					type="button"
					className="btn btn-sm btn-primary"
					style={{ marginLeft: "auto" }}
					onClick={handleCreate}
					disabled={templates.length === 0}
					title={templates.length === 0 ? "Create a template first" : undefined}
				>
					<Icon name="add" size={16} /> Create Schedule
				</button>
			</Card.Header>
			<Card.Body style={{ padding: 0 }}>
				{schedules.length === 0 ? (
					<p className="text-muted" style={{ padding: 16, marginBottom: 0 }}>
						No schedules yet. Create a template first, then schedule automated
						delivery.
					</p>
				) : (
					<table className="table" style={{ marginBottom: 0 }}>
						<thead>
							<tr>
								<th>Template</th>
								<th>Frequency</th>
								<th>Recipients</th>
								<th>Enabled</th>
								<th>Last Sent</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{schedules.map((s) => (
								<tr key={s.id}>
									<td>{s.templateName}</td>
									<td style={{ textTransform: "capitalize" }}>
										{s.frequency}
										{s.frequency === "weekly" &&
											s.dayOfWeek != null &&
											` (${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][s.dayOfWeek]})`}
										{s.frequency === "monthly" &&
											s.dayOfMonth != null &&
											` (day ${s.dayOfMonth})`}
									</td>
									<td>{s.recipients.join(", ")}</td>
									<td>
										<Form.Check
											type="switch"
											checked={s.enabled}
											onChange={() => handleToggle(s)}
										/>
									</td>
									<td>
										{s.lastSentAt
											? new Date(s.lastSentAt).toLocaleDateString()
											: "Never"}
									</td>
									<td>
										<button
											type="button"
											className="btn btn-sm btn-outline-danger"
											onClick={() => deleteSchedule.mutate(s.id)}
											disabled={deleteSchedule.isPending}
										>
											Delete
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Card.Body>
		</Card>
	);
};

const BrandingTab: FC = () => {
	const { data: branding } = useReportBranding();
	const { updateBranding, generateReport } = useReportActions();
	const [form, setForm] = useState<MetaAdsReportBranding>(branding);

	const brandingDirty =
		form.accentColor !== branding.accentColor ||
		form.headerText !== branding.headerText ||
		form.footerText !== branding.footerText ||
		form.showLogo !== branding.showLogo;

	useRegisterMetaAdsUnsavedWork(
		brandingDirty,
		"Unsaved report branding changes",
	);

	const handleSave = () => {
		updateBranding.mutate(form);
	};

	const handlePreview = async () => {
		const today = new Date();
		const to = today.toISOString().split("T")[0];
		const from = new Date(today.getTime() - 29 * 86400000)
			.toISOString()
			.split("T")[0];

		try {
			const blob = await generateReport.mutateAsync({ from, to });
			const url = URL.createObjectURL(blob);
			window.open(url, "_blank");
		} catch {
			// error handled by mutation
		}
	};

	return (
		<Card>
			<Card.Header>Report Branding</Card.Header>
			<Card.Body>
				<Form.Group className="mb-3">
					<Form.Label>Accent Color</Form.Label>
					<div className="d-flex gap-2 align-items-center">
						<Form.Control
							type="color"
							value={form.accentColor || "#206bc4"}
							onChange={(e) =>
								setForm({ ...form, accentColor: e.target.value })
							}
							style={{ width: 50, height: 38, padding: 2 }}
						/>
						<Form.Control
							type="text"
							value={form.accentColor || ""}
							onChange={(e) =>
								setForm({ ...form, accentColor: e.target.value })
							}
							placeholder="Default: company brand color"
							style={{ maxWidth: 200 }}
						/>
					</div>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Header Text</Form.Label>
					<Form.Control
						value={form.headerText || ""}
						onChange={(e) => setForm({ ...form, headerText: e.target.value })}
						placeholder="Default: company name"
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Footer Text</Form.Label>
					<Form.Control
						value={form.footerText || ""}
						onChange={(e) => setForm({ ...form, footerText: e.target.value })}
						placeholder="Custom footer text"
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Check
						type="switch"
						label="Show company logo in report"
						checked={form.showLogo}
						onChange={(e) => setForm({ ...form, showLogo: e.target.checked })}
					/>
				</Form.Group>

				<div className="d-flex gap-2">
					<button
						type="button"
						className="btn btn-primary"
						disabled={updateBranding.isPending}
						onClick={handleSave}
					>
						{updateBranding.isPending ? "Saving..." : "Save Branding"}
					</button>
					<button
						type="button"
						className="btn btn-outline-primary"
						disabled={generateReport.isPending}
						onClick={handlePreview}
					>
						{generateReport.isPending ? "Generating..." : "Preview PDF"}
					</button>
				</div>
			</Card.Body>
		</Card>
	);
};

type Tab = "templates" | "schedules" | "branding";

const ReportsTabs: FC = () => {
	const [activeTab, setActiveTab] = useState<Tab>("templates");
	const canManage = useCanManageMetaAds();
	const tabs: { key: Tab; label: string }[] = canManage
		? [
				{ key: "templates", label: "Templates" },
				{ key: "schedules", label: "Schedules" },
				{ key: "branding", label: "Branding" },
			]
		: [{ key: "templates", label: "Templates" }];

	return (
		<>
			<ul className="nav nav-tabs">
				{tabs.map((tab) => (
					<li key={tab.key} className="nav-item">
						<button
							type="button"
							className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
							onClick={() => setActiveTab(tab.key)}
						>
							{tab.label}
						</button>
					</li>
				))}
			</ul>

			<ErrorBoundary
				fallback={
					<Card>
						<Card.Body>
							<p className="text-muted" style={{ marginBottom: 0 }}>
								Unable to load report settings.
							</p>
						</Card.Body>
					</Card>
				}
			>
				<Suspense fallback={<p>Loading...</p>}>
					{activeTab === "templates" && <TemplatesTab />}
					{canManage && activeTab === "schedules" && <SchedulesTab />}
					{canManage && activeTab === "branding" && <BrandingTab />}
				</Suspense>
			</ErrorBoundary>
		</>
	);
};

export const PageMetaAdsReports: FC = () => {
	return (
		<AppPage title="Meta Ads Reports">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<Suspense fallback={<p>Loading...</p>}>
					<ReportsTabs />
				</Suspense>
			</div>
		</AppPage>
	);
};
